import Button from "@/components/common/Button";
import FloatingFAQButton from "@/components/common/FloatingFAQButton";
import toast from "react-hot-toast";
import { useAuth } from "@/hooks/useAuth";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { setPendingVerification } from "@/utils/secureStorage";

const schema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

type FormValues = z.infer<typeof schema>;

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionExpired = params.get("sessionExpired");
  const { loginUser } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isCompanyLogin, setIsCompanyLogin] = useState(
    location.pathname.includes("/company") ||
      location.search.includes("type=company")
  );

  useEffect(() => {
    console.log("Login type changed to:", isCompanyLogin ? "Company" : "User");
  }, [isCompanyLogin]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>();

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);

      console.log("Attempting login as:", isCompanyLogin ? "Company" : "User");
      console.log("Login data:", { ...data, isCompanyLogin });

      const result = await loginUser(data.email, data.password, isCompanyLogin);
      console.log("Login result:", result);

      // if (!result.user?.isVerified || !result.user?.isActive) {
      //   setError("Please verify your email before logging in.");
      //   return;
      // }

      if (result.requiresOtp) {
        console.log("OTP required, redirecting to verification page");

        // Store email and userId for OTP verification
        setPendingVerification(data.email, result.userId || "");

        // Show success message about OTP being sent
        toast.success("OTP sent to your email. Please verify to continue.");

        // Navigate to OTP verification page with state
        const state = {
          email: data.email,
          userId: result.userId,
          loginType: isCompanyLogin ? "company" : "user",
        };
        console.log("Navigating to OTP verification with state:", state);
        navigate("/verify-otp", {
          state,
          replace: true,
        });
        return;
      }

      // If no OTP required, proceed with normal login
      console.log("No OTP required, proceeding with normal login");
      console.log("User data:", result.user);
      console.log("User role:", result.user?.role);
      console.log("Is company login:", isCompanyLogin);

      // Redirect based on user role
      let redirectPath = "/dashboard"; // default fallback

      if (isCompanyLogin) {
        redirectPath = "/admin/company";
      } else {
        // For user login, check the actual role from backend
        switch (result.user?.role) {
          case "employee":
            redirectPath = "/employee/dashboard";
            break;
          case "department_admin":
            redirectPath = "/department-admin/dashboard";
            break;
          case "company_admin":
            redirectPath = "/admin/company";
            break;
          default:
            // If no role or unknown role, default to employee dashboard
            redirectPath = "/employee/dashboard";
            break;
        }
      }

      console.log("Redirecting to:", redirectPath);
      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      console.error("Login error:", err);
      const backendMsg = err?.response?.data?.message;
      const statusCode = err?.response?.status;

      if (backendMsg) {
        // Handle specific error cases with better UX
        if (
          backendMsg.toLowerCase().includes("invalid email or password") ||
          backendMsg.toLowerCase().includes("invalid credentials") ||
          backendMsg.toLowerCase().includes("incorrect credentials")
        ) {
          toast.error(t("auth.invalidCredentials"));
        } else if (
          backendMsg.toLowerCase().includes("not approved") ||
          backendMsg.toLowerCase().includes("not active")
        ) {
          toast.error(t("auth.notApproved"));
        } else if (backendMsg.includes("OTP")) {
          toast.success(backendMsg); // Let OTP messages show as-is
        } else {
          // Show exact backend message for other cases (like email verification)
          toast.error(backendMsg);
        }
      } else if (statusCode && (statusCode === 400 || statusCode === 500)) {
        toast.error(t("auth.loginFailed"));
      } else {
        toast.error(t("auth.loginFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLoginType = () => {
    setIsCompanyLogin(!isCompanyLogin);

    reset();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      {sessionExpired && (
        <div className="mb-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-700">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Session Expired</h3>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">Your session is expired, please login again.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isCompanyLogin ? t("auth.loginCompany") : t("auth.login")}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {t("auth.noAccount")}{" "}
          <Link
            to="/register-company"
            className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
          >
            {t("auth.register")}
          </Link>
        </p>
        <button
          type="button"
          onClick={toggleLoginType}
          className="mt-2 text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
        >
          {isCompanyLogin
            ? t("auth.switchToUserLogin")
            : t("auth.switchToCompanyLogin")}
        </button>
      </div>



      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            {isCompanyLogin ? t("auth.primaryEmail") : t("auth.email")}
          </label>
          <div className="mt-1">
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              placeholder={
                isCompanyLogin ? "company@example.com" : "user@example.com"
              }
              className={`block w-full rounded-md border ${
                errors.email
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
              } px-3 py-2 placeholder-gray-400 shadow-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400`}
              {...register("email", { required: true })}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            {t("auth.password")}
          </label>
          <div className="mt-1">
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                className={`block w-full rounded-md border ${
                  errors.password
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                } px-3 py-2 placeholder-gray-400 shadow-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400`}
                {...register("password", { required: true })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-white"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-red-600">
                {errors.password.message}
              </p>
            )}
          </div>
          <p className="mt-2 text-sm text-right">
            <Link
              to={
                isCompanyLogin
                  ? "/company/forgot-password"
                  : "/user/forgot-password"
              }
              className="text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Forgot Password?
            </Link>
          </p>
        </div>

        <div>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
          >
            {t("auth.login")}
          </Button>
        </div>
      </form>

      {/* Floating FAQ Button */}
      <FloatingFAQButton />
    </motion.div>
  );
};

export default Login;
